import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { IOption } from 'shared/components/Dropdown';
import {
  ClinicUserClinicFragment,
  PaymentMedium,
  PaymentRelationship,
  StripePaymentIntentStatus,
  StripePaymentMethodType,
  StripePaymentOutputFragment,
  useCancelStripePaymentIntentMutation,
  useClearTerminalDisplayMutation,
  useCreateStripePaymentIntentMutation,
  useGetPaymentIntentStatusQuery,
  useGetStripePaymentIntentQuery,
  useProcessTerminalPaymentMutation,
  useSubscribeToStripePaymentIntentChangedSubscription,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import InvoiceStatusMessage from 'shared/components/SidePanel/components/InvoicingPanel/components/InvoiceStatusMessage';
import { ConnectionStatus, InvoiceStatus, useStripeTerminalProvider } from 'shared/providers/StripeTerminalProvider';
import { email } from 'shared/utils/validation';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import usePersistedState from 'shared/hooks/usePersistedState';
import { StripeCardElementChangeEvent, StripeElement } from '@stripe/stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { Box, Button, VStack, Tooltip, Divider, MenuItemProps, Spinner, useToast } from '@televet/kibble-ui';
import { getInvoiceAmountDue } from 'shared/components/SidePanel/components/InvoicingPanel/utils/InvoicingPanelFunctions';
import InvoiceAndARDisplay from './InvoiceAndARDisplay';
import LoadingSpinner from './PaymentHelperComponents/LoadingSpinner';
import { StripeTerminalClinicPetParent } from 'shared/providers/StripeTerminalProvider/types/StripeTerminalClinicPetParent';
import { useAppSelector } from 'state/hooks';
import {
  CreditCardFields,
  defaultCardValidationState,
  PAYMENT_MEDIUM_MAP,
  STRIPE_ERROR_READABLE,
} from './utils/paymentEnumsAndVariables';
import { useManageTerminals } from './hooks/useManageTerminals';
import {
  calculateClientServiceFeePercent,
  getTooltipMessage,
  IGetTooltipMessageInput,
} from './utils/paymentHelperFunctions';
import TabsWrapper from './PaymentHelperComponents/TabsWrapper';
import ReceiptInput from './PaymentHelperComponents/ReceiptInput';
import { UseCareBenefitFollowupStep } from '../../hooks/useCareBenefitFollowup';

interface IPaymentProps {
  clinicPetParent?: StripeTerminalClinicPetParent;
  updateInvoiceCost?: (invoiceTotal: number) => void;
  setSelectedInvoiceId: (invoiceId: string | null) => void;
  unCompletedFollowUpSteps: UseCareBenefitFollowupStep[] | undefined;
  completeUncompletedFollowUpSteps: (id: string) => Promise<void>;
  setIsPaymentSuccessful: React.Dispatch<React.SetStateAction<boolean>> | undefined;
}

const Payment = ({
  clinicPetParent,
  updateInvoiceCost,
  setSelectedInvoiceId,
  unCompletedFollowUpSteps,
  completeUncompletedFollowUpSteps,
  setIsPaymentSuccessful,
}: IPaymentProps): JSX.Element => {
  const { currentClinic: currentProviderClinic } = useClinicUser();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [tabIndex, setTabIndex] = useState(0);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState<string>();
  const [selectedSavedPaymentMethodStripeId, setSelectedSavedPaymentMethodStripeId] = useState<string | null>(null);
  const toast = useToast();
  const careWizardDraft = useAppSelector((state) => state.CareWizardSteps.careWizardDraft);
  const { paymentDue, selectedInvoiceId, careAccountCreditIntentId, isBalanceSelected } = careWizardDraft;
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const [isCardSetForSaving, setIsCardSetForSaving] = useState(false);
  // Credit card payment validation message
  const [validationMessage, setValidationMessage] = useState('');

  // Retrieve Stripe input elements
  const elements = useElements();

  // Keep track of credit card validation state
  const [isValid, setIsValid] = useState(defaultCardValidationState);

  // Track whether or not the invoice is in progress, so we can make sure the user doesn't try to cancel it before it reaches the terminal
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);

  const [receiptEmail, setReceiptEmail] = useState<string>();

  const stripe = useStripe();

  // Retrieve Stripe information and functionality for use in transactions
  const {
    terminal,
    getErrorMessage,
    terminalOptions,
    isInitialized,
    disconnectReader,
    resetTerminalList,
    currentInvoiceStatus,
    setCurrentInvoiceStatus,
    selectedPetParent,
    currentPaymentMedium,
    setSelectedPetParent,
    setCurrentPaymentMedium,
    amountToInvoice,
    setAmountToInvoice,
    setInvoiceIds,
    invoiceIds,
    setPaymentRelationship,
    paymentRelationship,
  } = useStripeTerminalProvider();

  const { terminalOption, setTerminalOption, setLastSelectedTerminalId } = useManageTerminals(terminalOptions);

  const hasInvoices = useMemo(() => !!invoiceIds?.length && invoiceIds[0] !== '', [invoiceIds]);

  useEffect(() => {
    if (!!isBalanceSelected) {
      setPaymentRelationship(PaymentRelationship.AccountBalance);
    } else if (hasInvoices) {
      setPaymentRelationship(null);
    } else {
      setPaymentRelationship(PaymentRelationship.CustomAmount);
    }
  }, [isBalanceSelected, paymentRelationship, hasInvoices, setPaymentRelationship]);

  useEffect(() => {
    // initialize
    if (!selectedPetParent) {
      setSelectedPetParent(clinicPetParent);
    }
    if (!invoiceIds?.length && selectedInvoiceId) {
      setInvoiceIds([selectedInvoiceId]);
    }
  }, [
    clinicPetParent,
    paymentDue,
    selectedInvoiceId,
    invoiceIds,
    selectedPetParent,
    setSelectedPetParent,
    setInvoiceIds,
  ]);

  useEffect(() => {
    if (paymentDue) {
      const amountToInvoiceString = paymentDue.toFixed(2).toString();
      setAmountToInvoice(amountToInvoiceString);
    }
  }, [paymentDue, setAmountToInvoice]);

  const terminalMenuItems: MenuItemProps[] | undefined = useMemo(() => {
    // convert terminal options to menu items for use in select component
    return terminalOptions?.map((terminalOption) => ({
      label: terminalOption?.text || '',
      leftElement: (
        <Box
          bg={terminalOption?.isDisabled ? 'background.danger' : 'backgorund.success'}
          borderRadius="8px"
          display="inline-block"
          height="12px"
          width="12px"
        />
      ),
      value: terminalOption?.value?.id || '',
      isDisabled: terminalOption?.isDisabled,
    }));
  }, [terminalOptions]);

  const clinicPetParentId = useMemo(() => selectedPetParent?.id || '', [selectedPetParent]);

  // Subscribe to payment intent changed events.
  useSubscribeToStripePaymentIntentChangedSubscription({
    fetchPolicy: GraphQLFetchPolicies.NoCache,
    variables: { where: { node: { id: stripePaymentIntentId } } },
    skip: !stripePaymentIntentId || currentPaymentMedium !== PaymentMedium.StripeTerminal,
    onData: ({ data }) => {
      const { status, lastPaymentError } = data.data?.stripePaymentIntentChanged?.node ?? {};
      if (status === StripePaymentIntentStatus.Succeeded || status === StripePaymentIntentStatus.RequiresCapture) {
        setReceiptEmail('');
        setCurrentInvoiceStatus(InvoiceStatus.Received);
        if (unCompletedFollowUpSteps) {
          unCompletedFollowUpSteps.forEach(async (step) => {
            await completeUncompletedFollowUpSteps(step.id);
          });
        }
        if (setIsPaymentSuccessful) {
          // update UI
          setIsPaymentSuccessful(true);
        }
      } else if (lastPaymentError) {
        setCurrentInvoiceStatus(InvoiceStatus.Error);
        setErrorMessage(STRIPE_ERROR_READABLE[lastPaymentError]);
      }
    },
  });

  // The clinic that the transaction is for
  const currentClinic: ClinicUserClinicFragment | null | undefined = useMemo(() => {
    return currentProviderClinic;
  }, [currentProviderClinic]);

  const hasCustomizableFees = useMemo(
    () => !!currentClinic?.clinicSetting?.hasCustomizableFees,
    [currentClinic?.clinicSetting?.hasCustomizableFees],
  );
  const [clientServiceFeePercent, setClientServiceFeePercent] = useState<string | undefined>(undefined);

  useEffect(() => {
    const feePercent = calculateClientServiceFeePercent({
      hasCustomizableFees,
      currentClinic,
      currentPaymentMedium,
    });
    setClientServiceFeePercent(feePercent);
  }, [
    hasCustomizableFees,
    currentClinic?.clinicSetting?.paymentFeeConfig?.onlineClientServiceFeePercent,
    currentClinic?.clinicSetting?.paymentFeeConfig?.terminalClientServiceFeePercent,
    currentPaymentMedium,
    currentClinic,
  ]);

  const clientServiceFee = useMemo(
    () =>
      amountToInvoice && clientServiceFeePercent
        ? parseFloat(((parseFloat(clientServiceFeePercent) * parseFloat(amountToInvoice)) / 100).toFixed(2))
        : 0,
    [amountToInvoice, clientServiceFeePercent],
  );

  /**
   * The amount the client must pay, not including the clinic's fees
   * uses the imported getInvoiceAmountDue function defined in utils
   */
  const amountDue = useMemo(() => {
    return getInvoiceAmountDue({ amountToInvoice, clientServiceFee });
  }, [amountToInvoice, clientServiceFee]);

  // Show specific message for hovering over submit button
  const submitButtonTooltipMessage: string = useMemo(() => {
    const input: IGetTooltipMessageInput = {
      currentPaymentMedium,
      amountDue,
      terminalOption,
      selectedPetParent,
      isValid: isValid.card,
    };
    return getTooltipMessage(input);
  }, [currentPaymentMedium, amountDue, terminalOption, selectedPetParent, isValid.card]);

  const [createStripePaymentIntent, { loading: isPaymentIntentLoading }] = useCreateStripePaymentIntentMutation();

  // Cancel the created Stripe payment intent in the event of user cancellation or an error
  const [cancelStripePaymentIntent, { loading: cancelingIntent }] = useCancelStripePaymentIntentMutation({
    onError: (error) => {
      console.error('Error canceling payment intent: ', error);
      toast({
        title: 'Error canceling payment intent',
        description:
          'An error occurred while canceling this invoice. Please check the transactions table to view the current status.',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const [processTerminalPayment] = useProcessTerminalPaymentMutation({
    onError: (error) => {
      console.error('Network error sending payment to terminal', error);
      toast({
        title: 'Error sending payment to terminal',
        description: 'An error occurred when sending this payment to the terminal.',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const [clearTerminalDisplay, { loading: clearingTerminalDisplay }] = useClearTerminalDisplayMutation({
    onError: (error) => {
      console.error('Network error clearing terminal display', error);
      toast({
        title: 'Error clearing terminal display',
        description: 'An error occurred when clearing the terminal display.',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const isCancelingPayment = useMemo(
    () => cancelingIntent || clearingTerminalDisplay,
    [cancelingIntent, clearingTerminalDisplay],
  );

  const selectTerminalOption = (selectedItem: MenuItemProps | undefined): void => {
    const selectedTerm: IOption | undefined =
      !!selectedItem && selectedItem?.value
        ? terminalOptions?.find((t) => t.value.id === selectedItem.value)
        : undefined;
    setLastSelectedTerminalId(selectedTerm?.value?.id || '');
    setTerminalOption(selectedTerm);
  };

  const clearTerminalDisplayWithFallback = useCallback(async () => {
    let success = false;
    try {
      if (!terminalOption?.value.id) return;

      const response = await clearTerminalDisplay({
        variables: {
          stripeTerminalId: terminalOption.value.id,
        },
      });

      success = response.data?.clearTerminalDisplay?.success || false;

      if (!success) {
        console.error(response.data?.clearTerminalDisplay?.error);
      }
    } catch (e) {
      console.error('Failed to clear terminal display via server', e);
    }

    if (!terminal || success) {
      return;
    }

    if (terminal.getConnectionStatus() === ConnectionStatus.CONNECTED) {
      await disconnectReader();
    }

    try {
      terminal.cancelCollectPaymentMethod();
    } catch (e) {
      console.error('Failed to clear terminal display via local SDK', e);
    }
  }, [terminal, terminalOption, disconnectReader, clearTerminalDisplay]);

  // Cancel the created Stripe payment intent in the event of
  // user cancellation or an error and redirect if necessary.
  const cancelInvoice = useCallback(
    async (isError?: boolean) => {
      if (!isError) {
        setCurrentInvoiceStatus(InvoiceStatus.Canceled);
      }

      try {
        if (stripePaymentIntentId && currentPaymentMedium === PaymentMedium.StripeTerminal) {
          await cancelStripePaymentIntent({
            variables: {
              where: {
                id: stripePaymentIntentId,
              },
            },
          });
        }

        if (currentPaymentMedium === PaymentMedium.StripeTerminal) {
          await clearTerminalDisplayWithFallback();
        }
        setCurrentInvoiceStatus(InvoiceStatus.Canceled);
        setIsTransactionLoading(false);
      } catch (e) {
        console.error(e);
        captureException(e);
      }
    },
    [
      currentPaymentMedium,
      stripePaymentIntentId,
      setCurrentInvoiceStatus,
      cancelStripePaymentIntent,
      clearTerminalDisplayWithFallback,
    ],
  );

  const handleCreditCardInvoice = useCallback(
    async (paymentIntentData: StripePaymentOutputFragment) => {
      try {
        if (!(stripe && elements && selectedPetParent)) {
          throw new Error('Stripe not properly set. Please refresh the page and try again.');
        }
        const cardElement: StripeElement | null = elements.getElement(CreditCardFields.Card);

        if (!cardElement && !selectedSavedPaymentMethodStripeId) {
          throw new Error('Stripe not properly set. Please refresh the page and try again.');
        }
        setCurrentPaymentMedium(PaymentMedium.StripeVirtualTerminal);
        const { paymentIntent: updatedIntent, error } = await stripe.confirmCardPayment(
          paymentIntentData.paymentIntentClientSecret || '',
          cardElement
            ? {
                payment_method: {
                  card: cardElement,
                },
                setup_future_usage:
                  (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal && isCardSetForSaving) ||
                  (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal && selectedSavedPaymentMethodStripeId)
                    ? 'off_session'
                    : undefined,
              }
            : { payment_method: selectedSavedPaymentMethodStripeId || '' },
        );
        if (updatedIntent?.status === 'succeeded') {
          setCurrentInvoiceStatus(InvoiceStatus.CardSuccessful);
        } else {
          setCurrentInvoiceStatus(InvoiceStatus.Error);
          const newErrorMessage = getErrorMessage(
            error || new Error('Stripe was not able to complete updating the intent'),
          );
          setErrorMessage(newErrorMessage || errorMessage);
          throw error;
        }
      } catch (e) {
        if (e instanceof Error) {
          setValidationMessage(e?.message || 'Whoops! Something went wrong while processing this payment.');
          console.error(e);
          await cancelInvoice(true);
          const newErrorMessage = getErrorMessage(e);
          setErrorMessage(newErrorMessage || errorMessage);
          captureException(e);
          setCurrentInvoiceStatus(InvoiceStatus.Error);
        }
      }
    },
    [
      stripe,
      elements,
      selectedPetParent,
      selectedSavedPaymentMethodStripeId,
      currentPaymentMedium,
      isCardSetForSaving,
      setCurrentInvoiceStatus,
      getErrorMessage,
      errorMessage,
      cancelInvoice,
      setCurrentPaymentMedium,
    ],
  );

  useEffect(() => {
    setCurrentPaymentMedium(PAYMENT_MEDIUM_MAP[tabIndex]);
  }, [currentPaymentMedium, tabIndex, setCurrentPaymentMedium]);

  useGetStripePaymentIntentQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: (data) => {
      if (data?.stripePaymentOutput) {
        handleTransaction(data?.stripePaymentOutput);
        setIsTransactionLoading(false);
      }
    },
    skip: !stripePaymentIntentId,
    onError: (error) => {
      console.error('Network error querying payment intent', error);
      toast({
        title: 'An error occurred in retrieving your stripe payment intent. Please try again.',
        status: 'error',
        duration: 5000,
      });
    },
    variables: {
      where: {
        id: stripePaymentIntentId,
      },
    },
  });

  const { stopPolling } = useGetPaymentIntentStatusQuery({
    variables: {
      where: {
        id: stripePaymentIntentId,
      },
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    skip: !stripePaymentIntentId,
    onCompleted: (data) => {
      // check for payment updates if web socket crashes subscription while waiting
      if (data.findUniqueStripePaymentIntent?.status && currentPaymentMedium === PaymentMedium.StripeTerminal) {
        if (
          [StripePaymentIntentStatus.Succeeded, StripePaymentIntentStatus.RequiresCapture].includes(
            data.findUniqueStripePaymentIntent.status,
          )
        ) {
          setReceiptEmail('');
          setCurrentInvoiceStatus(InvoiceStatus.Received);
          stopPolling();
        } else if (!!data.findUniqueStripePaymentIntent.lastPaymentError) {
          setCurrentInvoiceStatus(InvoiceStatus.Error);
          setErrorMessage(STRIPE_ERROR_READABLE[data.findUniqueStripePaymentIntent.lastPaymentError]);
          stopPolling();
        }
      } else {
        stopPolling();
      }
    },
    pollInterval: 5000,
    notifyOnNetworkStatusChange: true,
  });

  // Handle credit card invoice transaction if applicable or update invoice status
  // if it happened to not be handled in another part of the code (unlikely).
  const handleTransaction: (stripePaymentIntentData: StripePaymentOutputFragment) => Promise<void> = useCallback(
    async (stripePaymentIntentData: StripePaymentOutputFragment) => {
      if (!stripePaymentIntentData) {
        return;
      }
      if (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal) {
        handleCreditCardInvoice(stripePaymentIntentData);
        return;
      }

      if (stripePaymentIntentData.payment?.status === 'canceled') {
        setReceiptEmail('');
        setCurrentInvoiceStatus(InvoiceStatus.Canceled);
        return;
      }
    },
    [currentPaymentMedium, handleCreditCardInvoice, setCurrentInvoiceStatus],
  );

  // Create payment intent for a credit card payment.
  const handleSubmitDigitalInvoice = useCallback(async () => {
    try {
      if (!(selectedPetParent && (isValid.card || selectedSavedPaymentMethodStripeId))) return;
      setIsTransactionLoading(true);
      setCurrentInvoiceStatus(InvoiceStatus.CardProcessing);

      const amount = Math.round((amountDue - clientServiceFee) * 100);

      const feePercent = clientServiceFeePercent !== undefined ? parseFloat(clientServiceFeePercent) / 100 : undefined;

      const stripePaymentIntent = await createStripePaymentIntent({
        variables: {
          data: {
            amount,
            paymentMethodType: StripePaymentMethodType.CardNotPresent,
            clientServiceFeePercent: feePercent,
          },
          clinicPetParentId: selectedPetParent.id,
          paymentMedium: PaymentMedium.StripeVirtualTerminal,
          invoiceIds: isDiscoveredInvoiceDemoModeEnabled ? null : hasInvoices ? invoiceIds : null,
          paymentRelationship,
          // pass custom amount if its manual payment
          emailOverride: receiptEmail,
          careAccountCreditIntentId: careAccountCreditIntentId || undefined,
        },
      });
      if (!stripePaymentIntent?.data?.createStripePaymentIntent2) {
        setErrorMessage('Failed to create Stripe payment intent. Please try again.');
        setCurrentInvoiceStatus(InvoiceStatus.Error);
        throw new Error('Failed to create Stripe payment intent');
      }

      setStripePaymentIntentId(stripePaymentIntent?.data.createStripePaymentIntent2?.id);
    } catch (e) {
      if (e instanceof Error) {
        const newErrorMessage = getErrorMessage(e);
        setErrorMessage(newErrorMessage || errorMessage);
        setCurrentInvoiceStatus(InvoiceStatus.Error);
        captureException(e);
      }
    } finally {
      setIsTransactionLoading(false);
    }
  }, [
    selectedPetParent,
    isValid.card,
    selectedSavedPaymentMethodStripeId,
    setCurrentInvoiceStatus,
    amountDue,
    clientServiceFee,
    clientServiceFeePercent,
    createStripePaymentIntent,
    isDiscoveredInvoiceDemoModeEnabled,
    invoiceIds,
    hasInvoices,
    paymentRelationship,
    receiptEmail,
    careAccountCreditIntentId,
    getErrorMessage,
    errorMessage,
  ]);

  const showToastError = useCallback(
    (description: string): void => {
      toast({
        title: 'Error',
        description,
        status: 'error',
        duration: 5000,
      });
    },
    [toast],
  );

  const handleSubmitTerminalInvoice = useCallback(async () => {
    if (!amountDue)
      return showToastError('Please enter an amount above $0.50 and select a terminal and client for this invoice.');
    if (!terminalOption) return showToastError('Please select a terminal for this invoice.');
    if (!selectedPetParent) return showToastError('Please select a client for this invoice.');
    if (terminalOption?.value?.status !== 'online') return showToastError('Please select an online terminal.');
    if (!terminalOption?.value.id)
      return showToastError('Selected terminal is unavailable, please refresh your page and try again.');

    const amount = Math.round((amountDue - clientServiceFee) * 100);

    try {
      setCurrentInvoiceStatus(InvoiceStatus.Sending);
      setIsTransactionLoading(true);

      const feePercent = clientServiceFeePercent !== undefined ? parseFloat(clientServiceFeePercent) / 100 : undefined;
      const stripeTerminalId = terminalOption?.value.id;

      const response = await processTerminalPayment({
        variables: {
          stripeTerminalId,
          paymentIntent: {
            id: stripePaymentIntentId,
            create: {
              amount,
              paymentMethodType: StripePaymentMethodType.CardPresent,
              clientServiceFeePercent: feePercent,
            },
            clinicPetParentId: selectedPetParent.id,
            paymentMedium: PaymentMedium.StripeTerminal,
            invoiceIds: isDiscoveredInvoiceDemoModeEnabled ? null : hasInvoices ? invoiceIds : null,
            paymentRelationship,
            emailOverride: receiptEmail,
            careAccountCreditIntentId: careAccountCreditIntentId,
            // from payload
          },
        },
      });

      if (!response.data?.processTerminalPayment?.stripePaymentIntentId) {
        setCurrentInvoiceStatus(InvoiceStatus.Error);
        setErrorMessage('Failed to send payment to this terminal, please try again in a moment.');
        console.error('Terminal Invoice Failed to Create Payment Intent', response.data?.processTerminalPayment?.error);
        return;
      }

      setStripePaymentIntentId(response.data?.processTerminalPayment?.stripePaymentIntentId);

      if (!response.data?.processTerminalPayment?.success) {
        console.error('Terminal Invoice Failed - Allowing Retry', response.data?.processTerminalPayment?.error);
        setCurrentInvoiceStatus(InvoiceStatus.TerminalProcessRetry);
        return;
      }

      setCurrentInvoiceStatus(InvoiceStatus.Pending);
    } catch (e) {
      setErrorMessage(
        getErrorMessage(
          e,
          'An error occurred sending the payment to your terminal. Otto staff has been notified and are working to address this as soon as possible.',
        ),
      );
      setCurrentInvoiceStatus(InvoiceStatus.Error);
      captureException(e);
    } finally {
      setIsTransactionLoading(false);
    }
  }, [
    amountDue,
    showToastError,
    terminalOption,
    selectedPetParent,
    clientServiceFee,
    setCurrentInvoiceStatus,
    clientServiceFeePercent,
    processTerminalPayment,
    stripePaymentIntentId,
    isDiscoveredInvoiceDemoModeEnabled,
    invoiceIds,
    hasInvoices,
    paymentRelationship,
    receiptEmail,
    careAccountCreditIntentId,
    getErrorMessage,
  ]);

  // The receipt email that will receive the information on the transaction
  const handleChangeReceiptEmail = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setReceiptEmail(e.target.value);
  }, []);

  // Handle changing the saved credit card info for a digital payment
  const handleChangeCreditCard = useCallback((e: StripeCardElementChangeEvent) => {
    setValidationMessage('');
    const { complete, error } = e;

    if (complete && !error) {
      setIsValid((prevState) => ({ ...prevState, [e.elementType]: true }));
      return;
    }
    if (error) setValidationMessage(error?.message || 'Make sure you have provided valid credit card details.');
    setIsValid((prevState) => ({ ...prevState, [e.elementType]: false }));
  }, []);

  const isSubmitButtonDisabled = useMemo(() => {
    if (!currentPaymentMedium) return true;
    else if (currentPaymentMedium === PaymentMedium.StripeTerminal) {
      return (
        !(
          amountToInvoice &&
          parseFloat(amountToInvoice) >= 0.5 &&
          terminalOption &&
          selectedPetParent &&
          receiptEmail?.match(email)
        ) || currentInvoiceStatus !== InvoiceStatus.Default
      );
    } else if (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal) {
      return (
        !(
          amountToInvoice &&
          parseFloat(amountToInvoice) >= 0.5 &&
          (isValid.card || selectedSavedPaymentMethodStripeId) &&
          selectedPetParent &&
          receiptEmail?.match(email)
        ) || currentInvoiceStatus !== InvoiceStatus.Default
      );
    }
    return true;
  }, [
    currentPaymentMedium,
    amountToInvoice,
    terminalOption,
    selectedPetParent,
    receiptEmail,
    currentInvoiceStatus,
    isValid.card,
    selectedSavedPaymentMethodStripeId,
  ]);

  // Set selected pet parent when a new parent is selected from conversation
  useEffect(() => {
    if (selectedPetParent) setReceiptEmail(selectedPetParent.email || '');
    setSelectedSavedPaymentMethodStripeId(null);
  }, [selectedPetParent]);

  const syncTerminalList = useCallback(() => {
    setCurrentInvoiceStatus(InvoiceStatus.Default);
    setTerminalOption(undefined);
    resetTerminalList();
  }, [resetTerminalList, setCurrentInvoiceStatus, setTerminalOption]);

  const isSubmitPaymentButtonEnabled = useMemo(() => {
    return (
      currentInvoiceStatus !== InvoiceStatus.NoTerminalsFound &&
      currentInvoiceStatus !== InvoiceStatus.Pending &&
      currentInvoiceStatus !== InvoiceStatus.Sending &&
      currentInvoiceStatus !== InvoiceStatus.TerminalProcessRetry &&
      currentInvoiceStatus !== InvoiceStatus.CardProcessing
    );
  }, [currentInvoiceStatus]);

  const handleTabsChange = useCallback(
    (index: number): void => {
      setTabIndex(index);
      const paymentMedium = PAYMENT_MEDIUM_MAP[index];
      if (paymentMedium !== undefined) {
        setCurrentPaymentMedium(paymentMedium);
      }
    },
    [setCurrentPaymentMedium],
  );

  // Clear all fields in the form.
  const resetPanelState = useCallback(
    (isCanceling?: boolean, isError?: boolean) => {
      if (!isError) {
        if (!isCanceling) {
          setCurrentInvoiceStatus(InvoiceStatus.Default);
        } else setCurrentInvoiceStatus(InvoiceStatus.Canceled);
        setIsValid(defaultCardValidationState);
      }
      setStripePaymentIntentId('');
      setIsTransactionLoading(false);
      setValidationMessage('');
      setErrorMessage('');
    },
    [setCurrentInvoiceStatus],
  );

  // reset if user navigates away from invoicing panel without dismissing success message
  useEffect(() => {
    return (): void => resetPanelState();
  }, [resetPanelState]);

  // Show loading state if StripeTerminalProvider says we haven't finished initializing the online terminals
  if (!isInitialized) {
    return <LoadingSpinner loadingText="Fetching Payment Data" />;
  }

  return (
    <>
      <VStack alignItems="stretch">
        <InvoiceAndARDisplay
          isPaymentPage={true}
          setSelectedInvoiceId={setSelectedInvoiceId}
          clinicPetParent={clinicPetParent}
          updateInvoiceCost={updateInvoiceCost}
          paymentDue={paymentDue}
        />
        <Divider my="4" />
        <TabsWrapper
          tabIndex={tabIndex}
          handleTabsChange={handleTabsChange}
          isValid={isValid}
          clinicPetParentId={clinicPetParentId}
          currentPaymentMedium={currentPaymentMedium}
          currentInvoiceStatus={currentInvoiceStatus}
          terminalOptions={terminalOptions}
          terminalOption={terminalOption}
          terminalMenuItems={terminalMenuItems}
          selectTerminalOption={selectTerminalOption}
          syncTerminalList={syncTerminalList}
          isInitialized={isInitialized}
          selectedSavedPaymentMethodStripeId={selectedSavedPaymentMethodStripeId}
          setSelectedSavedPaymentMethodStripeId={setSelectedSavedPaymentMethodStripeId}
          isCardSetForSaving={isCardSetForSaving}
          setIsCardSetForSaving={setIsCardSetForSaving}
          handleChangeCreditCard={handleChangeCreditCard}
          validationMessage={validationMessage}
        />
        <Divider my="4" border="1px" />
        <ReceiptInput
          receiptEmail={receiptEmail}
          handleChangeReceiptEmail={handleChangeReceiptEmail}
          setValidationMessage={setValidationMessage}
        />
        <Divider mb="4" mt="2" border="1px" />
        {currentInvoiceStatus !== InvoiceStatus.Default && (
          <InvoiceStatusMessage
            clientFirstName={selectedPetParent?.firstName || 'this client'}
            clientLastName={selectedPetParent?.lastName || ''}
            status={currentInvoiceStatus}
            invoiceTotal={amountDue.toFixed(2)}
            paymentMethodType={
              currentPaymentMedium === PaymentMedium.StripeTerminal
                ? StripePaymentMethodType.CardPresent
                : StripePaymentMethodType.CardNotPresent
            }
            terminalName={terminalOption?.text || 'Unknown terminal'}
            dismissCallback={(): void => {
              if (currentInvoiceStatus === InvoiceStatus.NoTerminalsFound) {
                if (window.zE) {
                  window.zE('messenger', 'open');
                }
                return;
              }

              // Since we have a payment intent that's saved for manual retries,
              // we'll cancel it and clear the terminal display to be cleaned up.
              if (currentInvoiceStatus === InvoiceStatus.TerminalProcessRetry) {
                cancelInvoice(true);
              }

              resetPanelState(
                currentInvoiceStatus === InvoiceStatus.Canceled, // isCanceling
                currentInvoiceStatus === InvoiceStatus.Error ||
                  currentInvoiceStatus === InvoiceStatus.TerminalProcessRetry, // isError
              );

              setCurrentInvoiceStatus(InvoiceStatus.Default);
              if (currentInvoiceStatus === InvoiceStatus.Received) setReceiptEmail('');

              if (
                currentPaymentMedium === PaymentMedium.StripeVirtualTerminal &&
                currentInvoiceStatus === InvoiceStatus.CardSuccessful
              ) {
                setCurrentInvoiceStatus(InvoiceStatus.Default);
                if (unCompletedFollowUpSteps) {
                  unCompletedFollowUpSteps.forEach(async (step) => {
                    await completeUncompletedFollowUpSteps(step.id);
                  });
                }
                if (setIsPaymentSuccessful) {
                  setIsPaymentSuccessful(true);
                }
              }
            }}
            cancelCallback={cancelInvoice}
            retryCallback={handleSubmitTerminalInvoice}
            data-testid="invoice-status"
            errorMessage={errorMessage}
            isTransactionLoading={isTransactionLoading}
          />
        )}
        <Box
          position="absolute"
          bottom="0"
          pb="4"
          alignSelf="center"
          className="Payment__Container-Button-Make-Payment"
        >
          {isSubmitPaymentButtonEnabled && (
            <Tooltip label={submitButtonTooltipMessage}>
              <Button
                className="Payment__Submit-Button"
                isDisabled={isSubmitButtonDisabled}
                onClick={(): void => {
                  if (currentPaymentMedium && !isSubmitButtonDisabled) {
                    if (currentPaymentMedium === PaymentMedium.StripeTerminal) handleSubmitTerminalInvoice();
                    if (currentPaymentMedium === PaymentMedium.StripeVirtualTerminal) handleSubmitDigitalInvoice();
                  }
                }}
                cursor={isSubmitButtonDisabled ? 'not-allowed' : 'pointer'}
              >
                {isPaymentIntentLoading || isTransactionLoading || isCancelingPayment ? <Spinner /> : 'Make Payment'}
              </Button>
            </Tooltip>
          )}
        </Box>
      </VStack>
    </>
  );
};

export default Payment;
